'use client'

import { useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Lenis from 'lenis'
import { composer, ouvrir, raconter } from './choregraphie'

/**
 * L'orchestration de la page : l'ouverture, le récit au défilement, les
 * micro-interactions.
 *
 * ⭐ UNE SEULE LIGNE DE TEMPS, PAS UNE SOMME D'EFFETS. C'est toute la
 * différence entre une page qui s'ouvre et une page qui clignote : chaque
 * élément sait quand il entre PAR RAPPORT AUX AUTRES, et l'ensemble se termine
 * à un instant précis sur une page entièrement lisible. Six animations
 * indépendantes de durées voisines donnent un désordre que personne ne sait
 * corriger, parce qu'il n'y a rien à régler.
 *
 * ⛔⛔ L'ÉTAT DE DÉPART EST POSÉ EN CSS, PAS EN JAVASCRIPT, et c'est ce qui
 * évite le clignotement. La page est statique : elle s'affiche complète AVANT
 * que le moindre script ne s'exécute. Cacher les éléments depuis JavaScript les
 * ferait donc apparaître puis disparaître. Un script en ligne pose un attribut
 * sur la racine, le CSS cache ce qui doit entrer, et c'est cette orchestration
 * qui le lève.
 *
 * ⛔⛔ ET CE CHOIX A UN PRIX QU'IL FAUT PAYER EXPLICITEMENT : si l'animation ne
 * tourne pas, la page reste VIDE. 🟢 Mesuré le 2026-09-07 : dans une page dont
 * `visibilityState` vaut `hidden` — un onglet ouvert en arrière-plan, un volet
 * d'aperçu, un outil de capture — `requestAnimationFrame` ne rend **aucune
 * image** en une seconde entière. GSAP ne bat donc jamais, et tout ce que le
 * CSS cachait le reste. Aucune erreur, aucun avertissement : une page blanche.
 *
 * ⭐ D'où les trois filets, tous nécessaires et tous différents :
 *   1. le script en ligne se retire tout seul au bout de deux secondes et demie
 *      si personne ne prend la main ;
 *   2. ici, on n'annule ce délai QU'À L'INTÉRIEUR d'une image réellement
 *      rendue, c'est-à-dire une fois qu'on a la preuve que la boucle tourne ;
 *   3. et la ligne de temps elle-même est poussée à sa fin si elle n'a pas
 *      terminé à l'heure, au cas où la boucle s'arrêterait en cours de route.
 *
 * ⭐ `prefers-reduced-motion` : la 3D reste, le mouvement s'arrête.
 */
export function MiseEnScene() {
  useEffect(() => {
    const racine = document.documentElement
    const fenetre = window as Window & { __filetOuverture?: number }

    /*
     * ⛔⛔ LES ANCRES SE POSENT ICI, TOUT DE SUITE, ET HORS DES DEUX CONDITIONS.
     * Elles vivaient dans `demarrer`, c'est-à-dire derrière une image rendue ET
     * derrière le test de mouvement réduit. Deux trous, pas un :
     *
     *   1. là où aucune image n'arrive (onglet caché, volet d'aperçu), le
     *      sommaire redevenait natif EN SILENCE, donc l'historique se
     *      repolluait et le retour intelligent recassait ;
     *   2. et sous `prefers-reduced-motion`, le composant sort avant même
     *      d'appeler `demarrer` : le déplacement du focus, qui est dû dans TOUS
     *      les cas, n'était donc jamais posé pour qui a demandé moins de
     *      mouvement. Le chemin d'accessibilité était le seul non couvert.
     *
     * ⭐ Le gestionnaire ne dépend de l'orchestration que pour un détail (passer
     * par lenis quand il tourne), et cette dépendance passe par une boîte
     * mutable. Une capacité due dans tous les cas ne se pose pas derrière la
     * condition d'une capacité optionnelle.
     */
    const glissement: { lenis: Lenis | null } = { lenis: null }
    const ancres = new AbortController()
    poserAncres(glissement, ancres.signal)

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      clearTimeout(fenetre.__filetOuverture)
      racine.removeAttribute('data-anime')
      return () => ancres.abort()
    }

    let vivant = true
    let demonter: (() => void) | undefined

    const image = requestAnimationFrame(() => {
      if (!vivant) return
      clearTimeout(fenetre.__filetOuverture)
      /*
       * ⛔ Une première image qui arrive très tard veut dire que le filet du
       * script en ligne a DÉJÀ tout révélé, parce que la page était cachée. Y
       * rejouer l'ouverture re-cacherait une page que quelqu'un est peut-être
       * déjà en train de lire. On garde alors le récit et les
       * micro-interactions, et on saute l'entrée.
       */
      demonter = demarrer(racine, performance.now() > 2500, glissement)
    })

    return () => {
      vivant = false
      cancelAnimationFrame(image)
      ancres.abort()
      demonter?.()
      racine.removeAttribute('data-anime')
    }
  }, [])

  return null
}

/* -------------------------------------------------------------------------- */

/** Monte tout, et rend de quoi tout démonter. */
function demarrer(
  racine: HTMLElement,
  tropTard: boolean,
  glissement: { lenis: Lenis | null },
): () => void {
  gsap.registerPlugin(ScrollTrigger)

  /*
   * ⛔ PAS DE DÉFILEMENT ADOUCI SUR TACTILE, et ce n'est pas une prudence : le
   * défilement natif d'iOS est meilleur que tout ce qu'on peut écrire. Il porte
   * l'inertie du doigt, le rebond de fin de course et l'interruption au
   * toucher, trois choses qu'une interpolation en JavaScript imite mal. Le
   * remplacer donne cette sensation de glissement mou qu'on reconnaît tout de
   * suite, et il casse le geste de retour de Safari.
   * ⚠️ `pointer: coarse` et non la largeur : c'est le doigt qui compte, pas
   * l'écran. Une tablette large est tactile, un petit portable ne l'est pas.
   */
  /* ⚠️ Un seul contrôleur pour TOUS les écouteurs posés ici. `gsap.context()`
     défait les animations, jamais les écouteurs du document : sans lui, chaque
     remontage en laisserait un derrière, et personne ne le verrait. */
  const arret = new AbortController()

  const tactile = window.matchMedia('(pointer: coarse)').matches
  const lenis = tactile ? null : new Lenis({ duration: 1.05, smoothWheel: true })
  glissement.lenis = lenis
  let battre: ((temps: number) => void) | null = null
  if (lenis) {
    lenis.on('scroll', ScrollTrigger.update)
    battre = (temps: number) => lenis.raf(temps * 1000)
    gsap.ticker.add(battre)
    /* ⚠️ Sans ça, GSAP « rattrape » les images perdues après un blocage du fil
       principal, et le défilement fait un bond au lieu de reprendre. */
    gsap.ticker.lagSmoothing(0)
  }

  let filet = 0
  let coupure: (() => void) | undefined
  const ctx = gsap.context(() => {
    /* ⚠️ Plus rien à poser quand l'ouverture est sautée : depuis le 2026-09-09,
       la gravure n'est plus pilotée par cette ligne de temps mais par l'arrivée
       de la scène, et sa valeur par défaut est déjà celle d'arrivée. */
    if (!tropTard) {
      const ouverture = ouvrir(racine)
      filet = ouverture.filet
      coupure = ouverture.coupure
    }
    raconter()
    composer()
  })

  return () => {
    clearTimeout(filet)
    coupure?.()
    arret.abort()
    ctx.revert()
    for (const t of ScrollTrigger.getAll()) t.kill()
    if (battre) gsap.ticker.remove(battre)
    glissement.lenis = null
    lenis?.destroy()
    document.querySelector('.poincon')?.classList.remove('cachet-pose')
  }
}

/* -------------------------------------------------------------------------- */

/**
 * Le sommaire : le défilement adouci quand lenis tourne, le focus toujours.
 *
 * ⭐ Posé synchronement au montage du composant, jamais derrière une image
 * rendue ni derrière le test de mouvement réduit. Les raisons sont écrites dans
 * `MiseEnScene`.
 */
function poserAncres(glissement: { lenis: Lenis | null }, signal: AbortSignal): void {
  /*
   * ⛔ LES ANCRES DOIVENT PASSER PAR LENIS QUAND IL TOURNE. Il pose
   * `scroll-behavior: auto !important` sur la racine pour ne pas se battre avec
   * le navigateur : un clic sur une ancre saute alors d'un coup, sans
   * transition, au milieu d'une page qui glisse partout ailleurs. L'incohérence
   * se remarque immédiatement.
   * ⭐ Délégué sur le document plutôt que posé sur chaque lien : la navigation
   * est rendue par React, ses liens vont et viennent, et un écouteur par lien
   * serait à reposer à chaque rendu.
   *
   * ⛔⛔ ET LE GESTIONNAIRE EXISTE MÊME SANS LENIS, parce qu'il fait DEUX choses
   * qui n'ont pas la même condition. Le défilement adouci n'a de sens que quand
   * lenis tourne ; le déplacement du focus, lui, est dû dans tous les cas.
   * Enfermer les deux dans la même condition privait le clavier du second sur
   * tactile, c'est-à-dire sur une tablette avec clavier externe, où lenis est
   * volontairement éteint. Une condition qui sert à deux règles finit toujours
   * par en desservir une.
   */
  document.addEventListener(
    'click',
    (e) => {
      const lien = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[data-ancre]')
      if (!lien) return
      const cible = document.querySelector(lien.getAttribute('href') ?? '')
      if (!cible) return

      /*
       * ⛔⛔ ON COUPE TOUJOURS LE COMPORTEMENT NATIF, ET C'EST LE BOUTON RETOUR
       * QUI L'EXIGE. Une ancre suivie nativement POUSSE UNE ENTRÉE DANS
       * L'HISTORIQUE. Sur tactile, où lenis est éteint volontairement, trois
       * clics dans le sommaire ajoutaient donc trois entrées, et le retour
       * intelligent ramenait à la troisième ancre au lieu de la page d'où l'on
       * venait. Il fallait appuyer quatre fois.
       *
       * ⚠️ Le défaut était asymétrique et invisible au bureau : là, lenis
       * coupait déjà le natif, donc l'historique restait propre. Il ne se
       * voyait qu'au doigt, et seulement après avoir utilisé le sommaire.
       *
       * ⭐ `scrollIntoView()` SANS option de comportement, exprès : il hérite du
       * `scroll-behavior` de la feuille, qui vaut `smooth` et repasse à `auto`
       * sous `prefers-reduced-motion`. Une seule source pour le mouvement, et
       * le glissement reste celui du navigateur, qui le fait mieux que nous.
       * L'écart de 24 px est rendu par `scroll-margin-top` en CSS, qui vaut
       * aussi pour une ancre atteinte au clavier ou depuis un lien externe.
       */
      e.preventDefault()
      const lenis = glissement.lenis
      if (lenis) lenis.scrollTo(cible as HTMLElement, { offset: -24 })
      else (cible as HTMLElement).scrollIntoView()

      /* ⚠️ Le focus suit le saut, avec ou sans lenis. Le navigateur pose bien un
         point de départ de tabulation sur la cible d'une ancre, donc l'ordre de
         tabulation ne casse pas ; mais il ne DÉPLACE pas le focus, et une
         synthèse vocale ne suit donc pas. S'en remettre à ce comportement
         serait en plus une dépendance non écrite de plus. */
      const ancre = cible as HTMLElement
      ancre.setAttribute('tabindex', '-1')
      ancre.focus({ preventScroll: true })
    },
    { signal },
  )
}
